'use client';
import React, { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useSiteSettings } from '@/components/SiteSettingsProvider';
import ConfirmationModal from '@/components/ConfirmationModal';

export default function Navbar() {
  const { phoneNumber: phone, showPhoneNumber } = useSiteSettings();
  const pathname = usePathname();
  const isContactPage = pathname === '/contact-us';
  const shouldShowPhone = showPhoneNumber || isContactPage;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lastBooking, setLastBooking] = useState<any>(null);
  const [isPopupAlertOpen, setIsPopupAlertOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadBooking = () => {
      const saved = localStorage.getItem('faremaps_last_booking');
      if (saved) {
        try {
          setLastBooking(JSON.parse(saved));
        } catch (e) {
          console.error('Failed to parse last booking:', e);
        }
      } else {
        setLastBooking(null);
      }
    };
    loadBooking();
    window.addEventListener('faremaps_booking_updated', loadBooking);
    window.addEventListener('storage', loadBooking);
    return () => {
      window.removeEventListener('faremaps_booking_updated', loadBooking);
      window.removeEventListener('storage', loadBooking);
    };
  }, []);

  const handleDismissLastBooking = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    localStorage.removeItem('faremaps_last_booking');
    setLastBooking(null);
    window.dispatchEvent(new Event('faremaps_booking_updated'));
  };

  const handleDownloadPDF = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!lastBooking) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setIsPopupAlertOpen(true);
      return;
    }

    const {
      bookingId,
      dateBooked,
      airline,
      departure,
      arrival,
      departTime,
      arriveTime,
      cabinClass,
      passengers = [],
      totalPrice = 0,
      contact = {},
    } = lastBooking;

    const basePriceTotal = totalPrice - 29 * passengers.length;
    const serviceFeeTotal = 29 * passengers.length;
    const PHONE = '+1 425-981-5705';

    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Faremaps Official Booking Receipt - ${bookingId}</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      background-color: #f8fafc;
      color: #334155;
    }
    table {
      border-collapse: collapse;
      width: 100%;
    }
    .wrapper {
      width: 100%;
      background-color: #f8fafc;
      padding: 20px 0;
    }
    .container {
      max-width: 680px;
      margin: 0 auto;
      background-color: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
    }
    .alert-header {
      background-color: #fffbeb;
      border-bottom: 1px solid #fde68a;
      padding: 14px 20px;
      text-align: center;
      font-size: 12px;
      line-height: 1.5;
      color: #b45309;
      font-weight: 700;
    }
    .alert-header a {
      color: #b45309;
      text-decoration: underline;
    }
    .main-header {
      background: #ffffff;
      background: linear-gradient(to bottom, #ffffff 0%, #f8fafc 100%);
      padding: 32px 24px;
      border-bottom: 1px solid #e2e8f0;
    }
    .booking-ref-card {
      background-color: #f1f5f9;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 16px 20px;
      margin-top: 18px;
    }
    .booking-ref-title {
      font-size: 10px;
      font-weight: 800;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }
    .booking-ref-val {
      font-size: 18px;
      font-weight: 900;
      color: #0ea5e9;
      letter-spacing: 0.5px;
    }
    .content-body {
      padding: 32px 24px;
    }
    .greeting {
      font-size: 18px;
      font-weight: 800;
      color: #0f172a;
      margin-bottom: 8px;
    }
    .intro-text {
      font-size: 13px;
      line-height: 1.6;
      color: #64748b;
      margin-bottom: 28px;
    }
    .section-title {
      font-size: 11px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #0f172a;
      border-left: 3px solid #0ea5e9;
      padding-left: 8px;
      margin-bottom: 16px;
    }
    .info-card {
      background-color: #f8fafc;
      border: 1px solid #f1f5f9;
      border-radius: 12px;
      padding: 18px;
      margin-bottom: 28px;
    }
    .info-grid td {
      padding: 6px 0;
      font-size: 13px;
    }
    .info-grid .label {
      color: #64748b;
      font-weight: 700;
      width: 120px;
    }
    .info-grid .value {
      color: #0f172a;
      font-weight: 800;
    }
    .flight-segment {
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;
      background-color: #ffffff;
    }
    .flight-header {
      border-bottom: 1px solid #f1f5f9;
      padding-bottom: 12px;
      margin-bottom: 16px;
    }
    .flight-route {
      font-size: 14px;
      font-weight: 850;
      color: #0f172a;
    }
    .flight-meta {
      font-size: 11px;
      font-weight: 700;
      color: #64748b;
      margin-top: 4px;
    }
    .airline-info {
      font-size: 13px;
      font-weight: 800;
      color: #0f172a;
      margin-bottom: 16px;
    }
    .airline-logo-badge {
      background-color: #f1f5f9;
      color: #0ea5e9;
      font-weight: 900;
      font-size: 11px;
      padding: 4px 8px;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
      display: inline-block;
    }
    .timeline-table td {
      vertical-align: top;
      font-size: 13px;
    }
    .timeline-time {
      font-weight: 800;
      color: #0f172a;
      white-space: nowrap;
    }
    .timeline-dot-col {
      width: 24px;
      text-align: center;
    }
    .timeline-dot {
      width: 8px;
      height: 8px;
      background-color: #0ea5e9;
      border-radius: 50%;
      display: inline-block;
      margin-top: 4px;
    }
    .timeline-line {
      width: 2px;
      height: 30px;
      background-color: #e2e8f0;
      margin: 2px auto;
    }
    .timeline-airport {
      font-weight: 700;
      color: #64748b;
      padding-bottom: 15px;
    }
    .timeline-airport-name {
      font-size: 11px;
      color: #94a3b8;
      font-weight: 600;
      margin-top: 2px;
    }
    .baggage-note {
      background-color: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      padding: 12px 16px;
      font-size: 11px;
      line-height: 1.5;
      color: #64748b;
      margin-bottom: 28px;
    }
    .data-table {
      width: 100%;
      margin-bottom: 28px;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      overflow: hidden;
    }
    .data-table th {
      background-color: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
      padding: 10px 14px;
      font-size: 10px;
      font-weight: 800;
      color: #475569;
      text-transform: uppercase;
      text-align: left;
    }
    .data-table td {
      padding: 12px 14px;
      font-size: 12px;
      border-bottom: 1px solid #f1f5f9;
      color: #334155;
    }
    .data-table tr:last-child td {
      border-bottom: none;
    }
    .price-breakdown {
      background-color: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 28px;
    }
    .price-row {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      padding: 8px 0;
      color: #64748b;
    }
    .price-row.total {
      border-top: 1px solid #e2e8f0;
      padding-top: 14px;
      margin-top: 6px;
      font-size: 16px;
      font-weight: 900;
      color: #0f172a;
    }
    .price-row.total .value {
      color: #0ea5e9;
    }
    .cc-box {
      background-color: #ffffff;
      border: 1px dashed #cbd5e1;
      border-radius: 10px;
      padding: 14px;
      margin-top: 14px;
      font-size: 11px;
      color: #475569;
    }
    .cc-title {
      font-weight: 800;
      color: #0f172a;
      margin-bottom: 4px;
    }
    .main-footer {
      background-color: #0f172a;
      color: #94a3b8;
      padding: 32px 24px;
      text-align: center;
      font-size: 11px;
      line-height: 1.6;
    }
    .footer-phone {
      font-size: 18px;
      font-weight: 900;
      color: #ffffff;
      margin-bottom: 4px;
    }
    .footer-email {
      font-size: 13px;
      color: #0ea5e9;
      font-weight: 700;
      text-decoration: none;
      margin-bottom: 20px;
      display: inline-block;
    }
    .footer-divider {
      height: 1px;
      background-color: #1e293b;
      margin: 20px auto;
      max-width: 200px;
    }
    @media print {
      body {
        background-color: #ffffff;
        padding: 0;
      }
      .wrapper {
        padding: 0;
        background-color: #ffffff;
      }
      .container {
        border: none;
        box-shadow: none;
        max-width: 100%;
      }
    }
  </style>
</head>
<body>
  <div class="wrapper">
    <div class="container">

      <div class="main-header">
        <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
          <tr>
            <td>
              <table cellpadding="0" cellspacing="0" border="0" style="width: 100%; margin-bottom: 20px;">
                <tr>
                  <td style="vertical-align: middle; text-align: left;">
                    <img src="https://faremaps.com/assets/images/logo/color1/full/color1_logo_transparent_background.png" alt="Faremaps Logo" style="height: 38px; width: auto; display: block; border: 0;" />
                  </td>
                  <td style="vertical-align: middle; text-align: right;">
                    <div style="font-size: 22px; font-weight: 900; letter-spacing: -0.5px; color: #0f172a; line-height: 1;">FARE<span style="color: #0ea5e9;">MAPS</span></div>
                    <div style="font-size: 8px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: 1.5px; margin-top: 3px;">Your Personal Travel Desk</div>
                  </td>
                </tr>
              </table>
              <h2 style="font-size: 20px; font-weight: 800; margin: 0; color: #0f172a;">Thank you for booking!</h2>
              <p style="font-size: 13px; color: #475569; margin: 6px 0 0 0; font-weight: 500;">Your booking is currently under processing.</p>
            </td>
          </tr>
        </table>
        <div class="booking-ref-card">
          <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
            <tr>
              <td>
                <div class="booking-ref-title">Booking Reference</div>
                <div class="booking-ref-val" style="color: #0ea5e9;">${bookingId}</div>
              </td>
              <td style="text-align: right;">
                <div class="booking-ref-title">Date Booked</div>
                <div style="font-size: 13px; font-weight: 800; color: #334155;">${dateBooked}</div>
              </td>
            </tr>
          </table>
        </div>
      </div>
      <div class="content-body">
        <div class="greeting">Dear ${passengers[0]?.title || 'Mr.'} ${passengers[0]?.firstName || ''} ${passengers[0]?.lastName || 'Valued Customer'},</div>
        <p class="intro-text">
          We are currently processing your ticket and will reach out to you if any additional information is required. Once confirmed by the airline, we will also send you your E-Ticket. Please be sure to check both your email inbox and spam folder for updates. Thank you for choosing our service.
        </p>
        <div class="section-title">Customer Information</div>
        <div class="info-card">
          <table cellpadding="0" cellspacing="0" border="0" class="info-grid">
            <tr>
              <td class="label">Primary Traveler</td>
              <td class="value">${passengers[0]?.firstName || ''} ${passengers[0]?.lastName || ''}</td>
            </tr>
            <tr>
              <td class="label">Phone</td>
              <td class="value">${contact.phone || PHONE}</td>
            </tr>
            <tr>
              <td class="label">Email Address</td>
              <td class="value">${contact.email || ''}</td>
            </tr>
            <tr>
              <td class="label">Citizenship</td>
              <td class="value">US Citizen</td>
            </tr>
          </table>
        </div>
        <div class="section-title">Flight Details</div>
        <div class="flight-segment">
          <div class="flight-header">
            <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
              <tr>
                <td>
                  <div class="flight-route">${departure} &rarr; ${arrival}</div>
                  <div class="flight-meta">${departTime} &bull; Cabin: ${cabinClass} &bull; Non Stop</div>
                </td>
              </tr>
            </table>
          </div>
          <div class="airline-info">
            <span class="airline-logo-badge">${(airline || '').slice(0, 2).toUpperCase()}</span>
            <span style="margin-left: 8px;">${airline} &bull; Economy/Coach</span>
          </div>
          <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
            <tr class="timeline-table">
              <td style="width: 80px;">
                <div class="timeline-time">${departTime}</div>
              </td>
              <td class="timeline-dot-col">
                <span class="timeline-dot"></span>
                <div class="timeline-line"></div>
              </td>
              <td class="timeline-airport">
                ${departure} Airport
                <div class="timeline-airport-name">Departure Gate Hub</div>
              </td>
            </tr>
            <tr class="timeline-table">
              <td>
                <div class="timeline-time">${arriveTime}</div>
              </td>
              <td class="timeline-dot-col">
                <span class="timeline-dot" style="background-color: #10b981;"></span>
              </td>
              <td class="timeline-airport" style="padding-bottom: 0;">
                ${arrival} Airport
                <div class="timeline-airport-name">Arrival Terminal Gate</div>
              </td>
            </tr>
          </table>
        </div>
        <div class="baggage-note">
          <strong>BAGGAGE DETAILS:</strong><br/>
          In the event of cancellation, our baggage fee stands non-refundable once the ticket is issued. In case of any queries, please contact us or read our Terms and conditions.
        </div>
        <div class="section-title">Traveler Details</div>
        <div class="data-table" style="overflow-x: auto;">
          <table cellpadding="0" cellspacing="0" border="0">
            <thead>
              <tr>
                <th>Type</th>
                <th>Title</th>
                <th>First Name</th>
                <th>Middle Name</th>
                <th>Last Name</th>
                <th>DOB</th>
                <th>Gender</th>
              </tr>
            </thead>
            <tbody>
              ${passengers
        .map(
          (p: any, i: number) => `
                <tr>
                  <td style="font-weight: 800; color: #0f172a;">${i === 0 ? 'Primary' : 'Adult'}</td>
                  <td>${p.title || '-'}</td>
                  <td style="font-weight: 700;">${p.firstName || ''}</td>
                  <td>${p.middleName || '-'}</td>
                  <td style="font-weight: 700;">${p.lastName || ''}</td>
                  <td>${p.dateOfBirth || '-'}</td>
                  <td>${p.gender || '-'}</td>
                </tr>
              `
        )
        .join('')}
            </tbody>
          </table>
        </div>
        <div class="section-title">Flight Price Details</div>
        <div class="price-breakdown">
          <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
            <tr>
              <td style="padding: 6px 0; font-size: 13px; color: #64748b; font-weight: 600; text-align: left;">
                ${passengers.length} x Ticket Price
              </td>
              <td style="padding: 6px 0; font-size: 13px; font-weight: 850; color: #0f172a; text-align: right;">
                $${basePriceTotal.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style="padding: 6px 0; font-size: 13px; color: #64748b; font-weight: 600; text-align: left;">
                Tax & Fee
              </td>
              <td style="padding: 6px 0; font-size: 13px; font-weight: 850; color: #0f172a; text-align: right;">
                $${serviceFeeTotal.toFixed(2)}
              </td>
            </tr>
            <tr>
              <td style="padding: 14px 0 0 0; font-size: 16px; font-weight: 900; color: #0f172a; border-top: 1px solid #e2e8f0; text-align: left;">
                Booking Amount
              </td>
              <td style="padding: 14px 0 0 0; font-size: 20px; font-weight: 900; color: #0ea5e9; border-top: 1px solid #e2e8f0; text-align: right;">
                $${totalPrice.toFixed(2)}
              </td>
            </tr>
          </table>
          <div class="cc-box">
            <table cellpadding="0" cellspacing="0" border="0" style="width: 100%;">
              <tr>
                <td style="vertical-align: top;">
                  <div class="cc-title">Billed By Faremaps</div>
                  <div style="font-size: 12px; font-weight: 800; color: #0f172a; margin-top: 4px;">
                    SECURE CARD - **** •••• &bull; Charged Amount: $${totalPrice.toFixed(2)}
                  </div>
                  <div style="margin-top: 8px; font-size: 10px; color: #64748b; line-height: 1.4;">
                    On your card statement, the charge will appear as: <strong>Faremaps Flight Booking</strong>.
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding-top: 14px; border-top: 1px solid #f1f5f9; margin-top: 10px;">
                  <div style="font-weight: 800; font-size: 9px; text-transform: uppercase; letter-spacing: 1.5px; color: #64748b; margin-bottom: 8px;">Accepted Payment Methods</div>
                  <table cellpadding="0" cellspacing="0" border="0">
                    <tr>
                      <td style="padding-right: 12px; vertical-align: middle;">
                        <img src="https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/visa.svg" alt="Visa" style="height: 22px; width: auto; display: block; border: 0;" />
                      </td>
                      <td style="padding-right: 12px; vertical-align: middle;">
                        <img src="https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/mastercard.svg" alt="Mastercard" style="height: 22px; width: auto; display: block; border: 0;" />
                      </td>
                      <td style="padding-right: 12px; vertical-align: middle;">
                        <img src="https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/amex.svg" alt="AMEX" style="height: 22px; width: auto; display: block; border: 0;" />
                      </td>
                      <td style="padding-right: 12px; vertical-align: middle;">
                        <img src="https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/discover.svg" alt="Discover" style="height: 22px; width: auto; display: block; border: 0;" />
                      </td>
                      <td style="padding-right: 12px; vertical-align: middle;">
                        <img src="https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/paypal.svg" alt="PayPal" style="height: 22px; width: auto; display: block; border: 0;" />
                      </td>
                      <td style="padding-right: 12px; vertical-align: middle;">
                        <img src="https://cdn.jsdelivr.net/npm/payment-icons@1.2.1/min/flat/jcb.svg" alt="JCB" style="height: 22px; width: auto; display: block; border: 0;" />
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
      <div class="main-footer">
        <div class="footer-phone"> Customer Support: ${PHONE}</div>
        <a href="mailto:support@faremaps.com" class="footer-email">support@faremaps.com</a>
        <div class="footer-divider"></div>
        <div>&copy; 2026 Faremaps Travel Desk. All rights reserved.</div>
      </div>
    </div>
  </div>
  <script>
    window.onload = function() {
      window.print();
      setTimeout(function() {
        window.close();
      }, 500);
    }
  </script>
</body>
</html>
`;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
            ? 'bg-slate-950/85 backdrop-blur-lg border-b border-white/10 shadow-premium py-2'
            : 'bg-slate-950/20 backdrop-blur-md border-b border-white/5 py-3'
          }`}
      >
        <div className="max-w-[1170px] mx-auto px-4 flex items-center justify-between h-[54px]">
          {/* Logo (inverted to white for cinematic luxury integration) */}
          <a href="/" className="flex items-center gap-2 flex-shrink-0 group">
            <img
              src="/assets/images/logo/color1/full/color1_logo_transparent_background.png"
              alt="Faremaps Logo"
              className="h-7 md:h-10 w-auto object-contain brightness-0 invert transition-transform duration-300 group-hover:scale-105"
            />
          </a>

          {/* Desktop support & call actions */}
          {shouldShowPhone && (
            <div className="hidden md:flex items-center gap-6">
              <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-full pl-3 pr-4 py-1.5 backdrop-blur-sm shadow-sm hover:border-accent/30 hover:bg-white/10 transition-all">
                {/* Live Indicator pulse */}
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-300">Support (24/7):</span>
                  <a
                    href={`tel:${phone}`}
                    className="font-black text-white hover:text-accent-light transition-colors"
                  >
                    {phone}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Mobile menu trigger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2 bg-accent/90 hover:bg-accent rounded-lg transition-all focus:ring-2 focus:ring-accent/50 focus:outline-none"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-5 h-0.5 bg-white transition-transform ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`}
            ></span>
            <span
              className={`block w-5 h-0.5 bg-white transition-opacity ${mobileOpen ? 'opacity-0' : ''}`}
            ></span>
            <span
              className={`block w-5 h-0.5 bg-white transition-transform ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`}
            ></span>
          </button>
        </div>

        {/* Mobile drawer menu */}
        {mobileOpen && (
          <div className="md:hidden bg-slate-950/95 backdrop-blur-lg border-b border-white/10 px-4 py-4 animate-fadeInUp">
            {shouldShowPhone && (
              <div className="flex items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-xl p-3">
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-2 w-2 flex-shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span className="text-[10px] text-slate-400 font-semibold leading-tight">
                    Toll-Free Call Support:
                  </span>
                </div>
                <a
                  href={`tel:${phone}`}
                  className="text-accent font-bold text-sm sm:text-base hover:text-accent-light whitespace-nowrap"
                >
                  {phone}
                </a>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Floating Bottom-Center Recent Booking Banner */}
      {lastBooking && (
        <div className="fixed bottom-6 left-0 right-0 z-[99999] flex justify-center pointer-events-none px-4">
          <div className="pointer-events-auto w-full max-w-[440px] md:w-max bg-slate-950/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] p-4 flex items-center justify-between gap-4 animate-[fadeInUp_0.4s_ease-out_forwards]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent/20 to-blue-500/20 border border-accent/30 flex items-center justify-center flex-shrink-0">
                <span className="text-lg">🎟️</span>
              </div>
              <div className="min-w-0 text-left">
                <span className="text-[10px] font-black text-sky-400 uppercase tracking-widest block leading-none mb-1">
                  Recent Booking
                </span>
                <span className="text-white font-extrabold text-xs block truncate leading-tight">
                  {lastBooking.departure?.slice(0, 3).toUpperCase()} &rarr;{' '}
                  {lastBooking.arrival?.slice(0, 3).toUpperCase()} ({lastBooking.bookingId})
                </span>
                <span className="text-[10px] text-slate-400 font-medium block mt-0.5">
                  {lastBooking.passengers?.length || 1} Traveler
                  {lastBooking.passengers?.length !== 1 ? 's' : ''} &bull; ${lastBooking.totalPrice}
                </span>
                <span className="text-[9px] text-sky-400/90 font-extrabold block mt-1 leading-none flex items-center gap-1">
                  <span>Check your email for more details</span>
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleDownloadPDF}
                className="bg-accent hover:bg-accent-dark text-white font-extrabold text-[11px] uppercase tracking-wider px-3.5 py-2 rounded-xl transition-all shadow-sm active:scale-95 flex items-center gap-1.5"
              >
                <span>Download</span>
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
              </button>
              <button
                onClick={handleDismissLastBooking}
                className="text-slate-400 hover:text-white hover:bg-white/10 p-1.5 rounded-lg transition-colors"
                aria-label="Dismiss Recent Booking"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isPopupAlertOpen}
        type="alert"
        title="Popups Blocked"
        message="Please allow popups to download your official booking receipt."
        onConfirm={() => setIsPopupAlertOpen(false)}
      />
    </>
  );
}
